import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

const STORAGE_KEY = 'geodatosarg_consent_v2';
const CHOICE_MADE_KEY = 'geodatosarg_consent_made';

function applyConsent(prefs) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    ad_storage: prefs.advertising ? 'granted' : 'denied',
    ad_user_data: prefs.advertising ? 'granted' : 'denied',
    ad_personalization: prefs.advertising ? 'granted' : 'denied',
    analytics_storage: prefs.analytics ? 'granted' : 'denied',
    functionality_storage: prefs.functional ? 'granted' : 'denied',
    personalization_storage: prefs.personalization ? 'granted' : 'denied',
    security_storage: 'granted',
  });
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'cookie_consent_update', consent: prefs });
}

export default function CookieBanner() {
  const { t } = useTranslation();
  const { lang = 'ar' } = useParams();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    functional: true,
    analytics: false,
    advertising: false,
    personalization: false,
  });

  useEffect(() => {
    try {
      const made = localStorage.getItem(CHOICE_MADE_KEY);
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setPrefs((p) => ({ ...p, ...parsed }));
      }
      if (!made) setVisible(true);
    } catch (e) {
      setVisible(true);
    }

    const handler = () => setShowPrefs(true);
    window.addEventListener('open-cookie-prefs', handler);
    return () => window.removeEventListener('open-cookie-prefs', handler);
  }, []);

  const persist = useCallback((next) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      localStorage.setItem(CHOICE_MADE_KEY, '1');
    } catch (e) {}
    applyConsent(next);
  }, []);

  const acceptAll = () => {
    const next = { necessary: true, functional: true, analytics: true, advertising: true, personalization: true };
    setPrefs(next); persist(next); setVisible(false); setShowPrefs(false);
  };
  const rejectAll = () => {
    const next = { necessary: true, functional: false, analytics: false, advertising: false, personalization: false };
    setPrefs(next); persist(next); setVisible(false); setShowPrefs(false);
  };
  const savePrefs = () => {
    persist(prefs); setVisible(false); setShowPrefs(false);
  };

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  return (
    <>
      {visible && !showPrefs && (
        <div className="cookie-banner fade-in" role="dialog" aria-live="polite" aria-label={t('cookies.bannerTitle')}>
          <div className="container-x py-5 md:py-6 flex flex-col lg:flex-row gap-5 lg:items-center lg:justify-between">
            <div style={{ maxWidth: 780 }}>
              <div className="font-serif" style={{ fontSize: 18, color: 'var(--gd-navy)', fontWeight: 600, marginBottom: 6 }}>
                {t('cookies.bannerTitle')}
              </div>
              <p style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.6 }}>
                {t('cookies.bannerBody')} <Link to={`/${lang}/cookies`} className="link-underline" style={{ color: 'var(--gd-navy)', fontWeight: 600 }}>{t('nav.cookies')}</Link>.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 lg:flex-nowrap">
              <button className="btn-outline" onClick={() => setShowPrefs(true)}>{t('cookies.manage')}</button>
              <button className="btn-outline" onClick={rejectAll}>{t('cookies.rejectAll')}</button>
              <button className="btn-primary" onClick={acceptAll}>{t('cookies.acceptAll')}</button>
            </div>
          </div>
        </div>
      )}

      {showPrefs && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowPrefs(false); }}>
          <div className="modal-card fade-in" role="dialog" aria-modal="true" aria-label={t('cookies.prefsTitle')}>
            <div style={{ padding: '24px 28px', borderBottom: '1px solid var(--gd-line)' }}>
              <div className="font-serif" style={{ fontSize: 22, color: 'var(--gd-navy)', fontWeight: 600 }}>{t('cookies.prefsTitle')}</div>
              <p style={{ fontSize: 14, color: '#475569', marginTop: 6 }}>{t('cookies.prefsIntro')}</p>
            </div>

            <div style={{ padding: '8px 28px' }}>
              {[
                { key: 'necessary', locked: true },
                { key: 'functional' },
                { key: 'analytics' },
                { key: 'advertising' },
                { key: 'personalization' },
              ].map((row) => (
                <div key={row.key} className="flex items-start justify-between gap-4" style={{ padding: '18px 0', borderBottom: '1px solid var(--gd-line)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: 'var(--gd-navy)' }}>{t(`cookies.${row.key}.title`)}</div>
                    <p style={{ fontSize: 13.5, color: '#475569', marginTop: 4, lineHeight: 1.55 }}>{t(`cookies.${row.key}.body`)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {row.locked ? (
                      <span style={{ fontSize: 11, color: 'var(--gd-terra)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {t('cookies.alwaysActive')}
                      </span>
                    ) : null}
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={row.locked ? true : !!prefs[row.key]}
                        disabled={!!row.locked}
                        onChange={() => !row.locked && toggle(row.key)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2" style={{ padding: '18px 28px 22px', borderTop: '1px solid var(--gd-line)' }}>
              <button className="btn-outline" onClick={rejectAll}>{t('cookies.rejectAll')}</button>
              <button className="btn-outline" onClick={acceptAll}>{t('cookies.acceptAll')}</button>
              <button className="btn-primary" onClick={savePrefs}>{t('cookies.save')}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
